import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer'
import LogoBup from '@BUP/assets/Images/Bup.png'
import { useTranslation } from 'react-i18next'
import { moment } from '@BUP/configs/i18n'

Font.register({
  family: 'Roboto',
  fonts: [
    { src: '/fonts/roboto/Roboto-Regular.ttf' },
    { src: '/fonts/roboto/Roboto-Bold.ttf' }
  ]
})

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Roboto',
    fontSize: 10
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 30
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 20
  },
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#E4E4E4'
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E4E4E4',
    justifyContent: 'center'
  },
  cellHeader: {
    backgroundColor: '#F5F5F5',
    fontWeight: 'bold',
    padding: 8,
    width: '25%',
    display: 'flex',
    alignItems: 'center'
  },
  cell: {
    padding: 8,
    width: '75%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  badgeContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8
  },
  badge: {
    fontSize: 10,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 3,
    alignSelf: 'center',
    textAlign: 'center',
    width: 'auto',
    height: 'auto'
  },
  badgeCyan: {
    backgroundColor: '#17bebb',
    color: 'white'
  },
  badgeRed: {
    backgroundColor: '#FFCDD2',
    color: '#C62828'
  },
  gold: {
    backgroundColor: '#d48806',
    color: 'white'
  },
  orange: {
    backgroundColor: 'orange',
    color: 'white'
  },
  volcano: {
    backgroundColor: '#ffbb96',
    color: 'white'
  },
  badgePurple: {
    backgroundColor: '#E1BEE7',
    color: '#6A1B9A'
  },
  logo: {
    width: 50,
    height: 50
  },
  statusDot: {
    width: 5,
    height: 5,
    borderRadius: 4,
    marginRight: 5
  },
  entriesWrapper: {
    display: 'flex',
    gap: '20'
  },
  entryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 5,
    textAlign: 'left'
  },
  signatureContainer: {
    marginTop: 100,
    textAlign: 'center',
    alignItems: 'center'
  },
  signatureLine: {
    width: '50%',
    height: 1,
    backgroundColor: 'black',
    marginBottom: 5
  },
  signatureText: {
    fontSize: 12,
    fontWeight: 'bold'
  },
  dateText: {
    fontSize: 12,
    marginTop: 10
  }
})

// Componente del PDF
export const ComplaintPDF = ({ complaint }) => {
  const {
    state, infractionLevel, infractionType, infractionTypeOtherDescription, createdAt, closedAt, updatedAt, complaintEntries, protocolType, nextStep
  } = complaint
  const i18nComplaint = useTranslation('translation', { keyPrefix: 'common.resources.complaint' })
  const i18nEntry = useTranslation('translation', { keyPrefix: 'common.resources.complaint-entry' })
  const infractionLevelText = i18nComplaint.t(`values.infraction-level.${infractionLevel}.label`)
  const infractionLevelColor = i18nComplaint.t(`values.infraction-level.${infractionLevel}.color`)
  const infractionTypeText = i18nComplaint.t(`values.infraction-type.${infractionType}.label`)

  const infractionTypeOtherText = `${i18nComplaint.t(`values.infraction-type.${infractionType}.label`)}`

  const StatusBadge = ({ status }) => {
    const statusColors = {
      open: '#1890ff',
      closed: '#d9d9d9'
    }

    return (
      <View style={styles.cell}>
        <View style={[styles.statusDot, { backgroundColor: statusColors[status] }]} />
        <Text>{statusText}</Text>
      </View>
    )
  }

  const statusText = i18nComplaint.t(`values.state.${state}.label`)
  return (
    <Document>
      <Page size='A4' style={styles.page}>
        {/* Header with logo */}
        <View style={styles.header}>
          <View style={{ width: '20%' }}>
            <Image src={LogoBup} style={styles.logo} />
          </View>
          <View style={{ width: '70%' }}>
            <Text style={[styles.title, { alignSelf: 'center' }]}>Reporte de denuncia
            </Text>
          </View>
        </View>
        {/* Table */}
        <View style={[styles.table, { marginBottom: 20 }]}>
          {/* Row Title */}
          <View style={styles.row} wrap={false}>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.title.label')}</Text>
            <Text style={styles.cell}>{complaint.title}</Text>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.state.label')}</Text>
            <StatusBadge status={state} />
          </View>
          {/* Row complainant */}
          <View style={styles.row} wrap={false}>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.complainants.label')}</Text>
            <View style={[styles.cell, styles.badgeContainer]}>
              {complaint.complainants.map((complainant, i) => (
                <Text key={i} style={[styles.badge, styles.badgeCyan]}>{complainant}</Text>
              ))}
            </View>
          </View>
          {/* Row infraction level and type */}
          <View style={styles.row} wrap={false}>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.infraction-type.label')}</Text>
            <View style={styles.cell}>
              <Text>{infractionType === 'cit007' ? `${infractionTypeOtherText}: ${infractionTypeOtherDescription}` : infractionTypeText}</Text>
            </View>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.infraction-level.label')}</Text>
            <View style={[styles.cell, styles.badgeContainer]}>
              <Text style={[styles.badge, styles[infractionLevelColor]]}>{infractionLevelText}</Text>
            </View>
          </View>
          {/* Row involved people and courses */}
          <View style={styles.row} wrap={false}>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.involved-people.label')}</Text>
            <View style={[styles.cell, styles.badgeContainer]}>
              {complaint.involvedPeople.map((person, i) => (
                <Text key={i} style={[styles.badge, styles.badgeRed]}>{person}</Text>
              ))}
            </View>
          </View>
          <View style={styles.row} wrap={false}>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.involved-courses.label')}</Text>
            <View style={[styles.cell, styles.badgeContainer]}>
              {complaint.involvedCourses.map((c, i) => (
                <Text key={i} style={[styles.badge, styles.badgePurple]}>{c}</Text>
              ))}
            </View>
          </View>
          {/* Row where and when */}
          <View style={styles.row} wrap={false}>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.description.events_time.label')}</Text>
            <Text style={[styles.cell, { textAlign: 'justify', wordBreak: 'break-word' }]}>{complaint.whereAndWhen}</Text>
          </View>
          {/* Row description */}
          <View style={styles.row} wrap={false}>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.description.detailed_description.label')}</Text>
            <Text style={[styles.cell, { textAlign: 'justify', wordBreak: 'break-word' }]}>{complaint.description}</Text>
          </View>
          {/* Row creation date */}
          <View style={styles.row} wrap={false}>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.created-at.label')}</Text>
            <Text style={styles.cell}>{moment(createdAt).format('LLLL')}</Text>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.created-by.label')}</Text>
            <Text style={styles.cell}>{complaint.creator.user.name}</Text>
          </View>
          {/* Row closing date */}
          <View style={styles.row} wrap={false}>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.closed-at.label')}</Text>
            <Text style={styles.cell}>{closedAt ? moment(closedAt).format('LLLL') : moment(updatedAt).format('LLLL')}</Text>
            <Text style={styles.cellHeader}>{i18nComplaint.t('attributes.closed-by.label')}</Text>
            <Text style={styles.cell}>{complaint.closer?.user?.name || 'N/A'}</Text>
          </View>
        </View>

        {protocolType && (
          <View style={{ marginVertical: 6 }}>
            <Text>
              <Text style={{ fontWeight: 'bold' }}>{i18nComplaint.t('attributes.protocol-type.label')}:</Text> <Text>{i18nComplaint.t(`values.protocol-type.${protocolType}.label`)}</Text>
            </Text>
          </View>)}

        {nextStep && (
          <View style={{ marginVertical: 6 }}>
            <Text>
              <Text style={{ fontWeight: 'bold' }}>{i18nComplaint.t('attributes.next-steps.label')}:</Text> <Text>{i18nComplaint.t(`values.next-steps.${complaint.nextStep}.label`)}</Text>
            </Text>
          </View>)}

        <View break />

        {/* Entries */}
        <Text style={styles.title}>{i18nEntry.t('label_one')}</Text>
        <View style={styles.entriesWrapper}>
          {complaintEntries.length > 0
            ? (
              <View style={styles.entriesWrapper}>
                {complaintEntries.map((entry, i) => (
                  <View key={i} wrap={false}>
                    <Text style={styles.entryTitle}>{i18nEntry.t('label')} {i + 1}</Text>
                    <View style={styles.table}>
                      {/* Detalles de cada entrada */}
                      <View style={styles.row} wrap={false}>
                        <Text style={styles.cellHeader}>{i18nEntry.t('attributes.type.label')}</Text>
                        <Text style={styles.cell}>{i18nEntry.t(`values.type.${entry.type}.label`)}</Text>
                      </View>
                      <View style={styles.row} wrap={false}>
                        <Text style={styles.cellHeader}>{i18nEntry.t('attributes.title.label')}</Text>
                        <Text style={styles.cell}>{entry.title || 'N/A'}</Text>
                      </View>
                      <View style={styles.row} wrap={false}>
                        <Text style={styles.cellHeader}>{i18nEntry.t('attributes.content.label')}</Text>
                        <Text style={[styles.cell, { textAlign: 'justify', wordBreak: 'break-word' }]}>{entry.content || 'N/A'}</Text>
                      </View>
                      <View style={styles.row} wrap={false}>
                        <Text style={styles.cellHeader}>{i18nEntry.t('attributes.created-at.label')}</Text>
                        <Text style={styles.cell}>{entry.createdAt ? moment(entry.createdAt).format('LLLL') : 'N/A'}</Text>
                      </View>
                      <View style={styles.row} wrap={false}>
                        <Text style={styles.cellHeader}>{i18nEntry.t('attributes.created-by.label')}</Text>
                        <Text style={styles.cell}>{entry.creator?.user?.name || 'N/A'}</Text>
                      </View>
                    </View>
                  </View>
                ))}
                <View style={styles.signatureContainer} break>
                  <View style={styles.signatureLine} />
                  <Text style={styles.signatureText}>{i18nEntry.t('attributes.signature.label')}</Text>
                  <Text style={styles.dateText}>{i18nEntry.t('attributes.date.label')}: __________________________</Text>
                </View>
              </View>
              )
            : (
              <View>
                <Text style={{ textAlign: 'center', marginTop: 10, marginBottom: 30 }}>{i18nEntry.t('label_two')}</Text>
                <View style={styles.signatureContainer} break>
                  <View style={styles.signatureLine} />
                  <Text style={styles.signatureText}>{i18nEntry.t('attributes.signature.label')}</Text>
                  <Text style={styles.dateText}>{i18nEntry.t('attributes.date.label')}: __________________________</Text>
                </View>
              </View>
              )}
        </View>
      </Page>
    </Document>
  )
}
